import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Autocomplete,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { feedService, birdsService } from '../services/apiServices';

const FeedCalculator: React.FC = () => {
  // keep inputs as strings to avoid controlled/uncontrolled and locale issues
  const [birds, setBirds] = useState<string>('');
  const [multiplier, setMultiplier] = useState<string>('1.0');
  const [selectedBird, setSelectedBird] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Fetch birds for autocomplete
  const { data: birdsData } = useQuery({
    queryKey: ['birds'],
    queryFn: () => birdsService.getAll(),
  });

  // Auto-populate bird count when bird is selected
  useEffect(() => {
    if (selectedBird && selectedBird.quantity_bought) {
      setBirds(selectedBird.quantity_bought.toString());
    }
  }, [selectedBird]);

  const mutation = useMutation({
    mutationFn: (payload: { weight_kg: number; birds_count?: number; multiplier?: number }) =>
      feedService.calculate(payload),
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Failed to calculate feed');
    },
    onSuccess: (data) => {
      setResult(data);
      // Optionally invalidate feed dashboard or related feed queries
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });

  const onCalculate = async () => {
    setError(null);
    setResult(null);

    const birdsVal = birds.trim() === '' ? 1 : parseInt(birds, 10);
    if (Number.isNaN(birdsVal) || birdsVal < 1) {
      setError('Please enter a valid number of birds (>= 1)');
      return;
    }

    const multiplierVal = multiplier.trim() === '' ? 1.0 : parseFloat(multiplier);
    if (Number.isNaN(multiplierVal) || multiplierVal <= 0) {
      setError('Please enter a valid multiplier (> 0)');
      return;
    }

    //mutation.mutate({ birds_count: birdsVal, multiplier: multiplierVal });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Feed Calculator
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            {/* Bird Selection */}
            <Autocomplete
              options={birdsData?.data || []}
              getOptionLabel={(option: any) => `${option.identifier} (${option.birds_type})`}
              value={selectedBird}
              onChange={(_, newValue) => setSelectedBird(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bird (optional)"
                  placeholder="Start typing to search birds..."
                  helperText="Select a bird to auto-populate weight and count"
                />
              )}
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <TextField
                label="Number of birds"
                fullWidth
                value={birds}
                type="number"
                slotProps={{ htmlInput: { step: '1', min: '1' } }}
                onChange={(e) => setBirds(e.target.value)}
              />
              <TextField
                label="Multiplier (activity)"
                fullWidth
                value={multiplier}
                type="number"
                slotProps={{ htmlInput: { step: '0.01', min: '0.1' } }}
                onChange={(e) => setMultiplier(e.target.value)}
              />
            </Box>
            <Button
              variant="contained"
              onClick={onCalculate}
              disabled={mutation.isPending}
              sx={{ maxWidth: { xs: '100%', md: 200 } }}
            >
              {mutation.isPending ? 'Calculating...' : 'Calculate'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6">Results</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography>Weight (kg): {result.weight_kg}</Typography>
              <Typography>Birds: {result.birds_count}</Typography>
              <Typography>Multiplier: {result.multiplier}</Typography>
              <Typography>Daily feed per bird: {result.daily_feed_g_per_bird} g</Typography>
              <Typography>Weekly feed per bird: {result.weekly_feed_kg_per_bird} kg</Typography>
              <Typography>Total weekly feed (flock): {result.total_weekly_feed_kg} kg</Typography>
              <Typography sx={{ mt: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
                Formula: {result.formula}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FeedCalculator;
